import { useState, useEffect, useCallback } from 'react';
import { getMemoryStatus } from '../utils/memoryCapacityApi';
import { logger } from '../utils/logger';

/**
 * @typedef {Object} MemoryCapacityState
 * @property {number} currentCount - Current number of memories
 * @property {number} freeLimit - Free memory limit
 * @property {number} purchasedSlots - Number of purchased slots
 * @property {number} totalCapacity - Total capacity (free + purchased)
 * @property {boolean} canCreate - Whether user can create more memories
 * @property {boolean} isAdmin - Server-flagged admin (unlimited slots)
 * @property {boolean} loading - Loading state
 * @property {string|null} error - Error message if any
 * @property {Function} refresh - Refresh the status
 */

/**
 * Hook for managing user memory capacity
 * @param {Object|null} user - Firebase user object
 * @returns {MemoryCapacityState}
 */
export function useMemoryCapacity(user) {
  const [status, setStatus] = useState({
    currentCount: 0,
    freeLimit: 10,
    purchasedSlots: 0,
    totalCapacity: 10,
    canCreate: true,
    isAdmin: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    // Anonymous users have no limit
    if (!user) {
      setStatus({
        currentCount: 0,
        freeLimit: 10,
        purchasedSlots: 0,
        totalCapacity: Infinity,
        canCreate: true,
        isAdmin: false,
      });
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getMemoryStatus(user);
      setStatus({
        currentCount: data.current_count,
        freeLimit: data.free_limit,
        purchasedSlots: data.purchased_slots,
        totalCapacity: data.total_capacity,
        canCreate: data.can_create,
        isAdmin: Boolean(data.is_admin),
      });
    } catch (err) {
      logger.error('Failed to fetch memory status:', err);
      setError(err.message);
      // Default to allowing creation on error (don't block user)
      setStatus(prev => ({ ...prev, canCreate: true, isAdmin: false }));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Refresh function for external use (e.g., after creating/deleting memory)
  const refresh = useCallback(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    ...status,
    loading,
    error,
    refresh,
  };
}
